/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import {
  BUTTON_FORMAT_TRANSPARENT,
  BUTTON_THEME_DEFAULT,
  ButtonProps,
  DIALOG_SIZE_LG,
  DIALOG_TYPE_WIZARD,
  DialogProps,
  WizardStep,
  WizardStepProps,
} from '@squiz/sds';
import { DialogLauncher } from 'apps/console-core/src/Components/DialogLauncher/DialogLauncher';
import { useDialogStore } from 'dxpConsoleCore-mf/State';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ADD_COMPONENT_TO_SET_DATA_IDS } from '../../../../Components/AddComponentToSet/AddComponentToSet.constants';
import { ComponentConfigurationForm } from '../../../../Components/AddComponentToSet/forms/ComponentConfigurationForm/ComponentConfigurationForm';
import {
  editableFormOptions,
  renderableFormOptions,
} from '../../../../Components/AddComponentToSet/forms/ComponentConfigurationForm/ComponentConfigurationForm.props';
import { COMPONENT_SERVICE_STRINGS } from '../../../../Constants/Strings.constants';
import { sortVersions } from '../../../../Utilities/SortVersions/SortVersions';
import { ComponentDetailsVersionsBadge } from '../../../ComponentDetails/elements/ComponentDetailsVersions/elements/ComponentDetailsVersionsBadge/ComponentDetailsVersionsBadge';
import { COMPONENT_SET_DETAILS_DATA_IDS } from '../../../ComponentSetDetails/ComponentSetDetails.constants';
import { ComponentDetailsWithinSetProps } from '../../ComponentDetailsWithinSet.props';
import { MAX_BADGES } from './ComponentDetailsVersionsSetManagement.constants';

/**
 * Component details version within a component set.
 */
export const ComponentDetailsVersionsSetManagement = (props: ComponentDetailsWithinSetProps): JSX.Element => {
  const { t } = useTranslation();

  const {
    selectedVersion,
    updateVersion,
    componentSetProps,
    componentDetails,
    updateComponentSetDetails,
    editableVersions,
    renderableVersions,
    excludedVersions,
    componentKey,
    disabled,
  } = props;

  const labels = useMemo(() => {
    return {
      and: t(COMPONENT_SERVICE_STRINGS.and),
      editableVersions: t(COMPONENT_SERVICE_STRINGS.editableVersions),
      excludedVersions: t(COMPONENT_SERVICE_STRINGS.excludedVersions),
      manage: t(COMPONENT_SERVICE_STRINGS.manage),
      manageComponentVersions: t(COMPONENT_SERVICE_STRINGS.manageComponentVersions),
      more: t(COMPONENT_SERVICE_STRINGS.more),
      noneFound: t(COMPONENT_SERVICE_STRINGS.noneFound),
      renderableVersions: t(COMPONENT_SERVICE_STRINGS.renderableVersions),
      save: t(COMPONENT_SERVICE_STRINGS.save),
      versionUsedIn: t(COMPONENT_SERVICE_STRINGS.versionUsedIn),
    };
  }, [t]);

  const createVersionBadges = (versions: Array<string>): Array<JSX.Element> => {
    versions = sortVersions(versions, 'desc');
    if (versions.length <= MAX_BADGES) {
      const badges = versions.map((version: string, index: number) => {
        return (
          <ComponentDetailsVersionsBadge
            isSelected={version === selectedVersion}
            key={version}
            label={version}
            updateVersion={updateVersion}
          />
        );
      });

      return badges;
    }

    const badges: Array<JSX.Element> = [];
    for (let i = 0; i < MAX_BADGES; i++) {
      badges.push(
        <ComponentDetailsVersionsBadge
          isSelected={versions[i] === selectedVersion}
          key={versions[i]}
          label={versions[i]}
          updateVersion={updateVersion}
        />,
      );
    }
    badges.push(
      <span>
        {labels.and} {(versions.length - MAX_BADGES).toString()} {labels.more}
      </span>,
    );
    return badges;
  };

  const configurationFormCustomProps = {
    componentID: `${componentDetails.namespace}/${componentDetails.name}`,
  };

  const createCurrentRenderableFormOption = (): renderableFormOptions => {
    if (componentSetProps.componentVersionRules[componentKey].renderableVersionPattern === '*') {
      return ADD_COMPONENT_TO_SET_DATA_IDS.renderableVersionsAll as renderableFormOptions;
    }
    return {
      RENDERABLE_VERSION_SPECIFY: componentSetProps.componentVersionRules[componentKey].renderableVersionPattern,
    };
  };

  const createCurrentEditableFormOption = (): editableFormOptions => {
    if (componentSetProps.componentVersionRules[componentKey].editableVersions[0] === 'latest') {
      return ADD_COMPONENT_TO_SET_DATA_IDS.editableVersionsLatest as editableFormOptions;
    }
    if (componentSetProps.componentVersionRules[componentKey].editableVersions.length === 0) {
      return ADD_COMPONENT_TO_SET_DATA_IDS.editableVersionsNone as editableFormOptions;
    }
    return { EDITABLE_VERSIONS_SPECIFIC: componentSetProps.componentVersionRules[componentKey].editableVersions };
  };

  // This is the function that just returns the component configuration form step
  const getWizardStep = (): Array<WizardStep> => {
    const webpath = componentSetProps.webPath;

    const componentConfiguration = {
      renderStep: (props: WizardStepProps): JSX.Element => {
        const componentConfigurationFormProps = {
          ...props,
          currentEditableFormOption: createCurrentEditableFormOption(),
          currentExcludedVersions: excludedVersions,
          currentRenderableFormOption: createCurrentRenderableFormOption(),
          customProps: configurationFormCustomProps,
          submitButtonText: labels.save,
          updateComponentSetDetails,
          webpath,
        };
        return <ComponentConfigurationForm {...componentConfigurationFormProps} />;
      },
    };

    return [componentConfiguration];
  };

  // This function launches the manage component versions dialog
  const manageVersionsLauncher = (): JSX.Element => {
    const buttonProps: ButtonProps = {
      dataId: COMPONENT_SET_DETAILS_DATA_IDS.componentDetailsVersionsSetManagementEditButton,
      disabled,
      format: BUTTON_FORMAT_TRANSPARENT,
      text: labels.manage,
      theme: BUTTON_THEME_DEFAULT,
    };

    const wizardProps: DialogProps = {
      classes: 'add-to-set__wizard',
      dialogSize: DIALOG_SIZE_LG,
      dialogType: DIALOG_TYPE_WIZARD,
      heading: labels.manageComponentVersions,
      scrollOnOverflow: true,
      stateHandler: useDialogStore,
      subtitle: labels.manageComponentVersions,
      wizardSteps: getWizardStep(),
    };

    return <DialogLauncher buttonProps={buttonProps} dialogProps={wizardProps} />;
  };

  // Information about editable versions
  const editableComponentVersions = (): JSX.Element => {
    return (
      <div className="component-details-set__versions-row-version-detail">
        <span className="component-details-set__versions-row-label">{labels.editableVersions} </span>
        <div className="component-details-set__versions-content">
          {editableVersions.length ? (
            createVersionBadges(editableVersions)
          ) : (
            <span className="component-details-set__versions-row-label-none-found">{labels.noneFound}</span>
          )}
        </div>
      </div>
    );
  };

  // Information about renderable versions
  const renderableComponentVersions = (): JSX.Element => {
    return (
      <div className="component-details-set__versions-row-version-detail">
        <div className="component-details-set__versions-content">
          <span className="component-details-set__versions-row-label">{labels.renderableVersions}</span>
          <div className="component-details-set__versions-content">
            {renderableVersions.length ? (
              createVersionBadges(renderableVersions)
            ) : (
              <span className="component-details-set__versions-row-label-none-found">{labels.noneFound}</span>
            )}
          </div>
        </div>
      </div>
    );
  };

  const excludedComponentVersions = (): JSX.Element => {
    return (
      <div className="component-details-set__versions-row-version-detail">
        <div className="component-details-set__versions-content">
          <span className="component-details-set__versions-row-label">{labels.excludedVersions}</span>
          <div className="component-details-set__versions-content">
            {excludedVersions.length ? (
              createVersionBadges(excludedVersions)
            ) : (
              <span className="component-details-set__versions-row-label-none-found">{labels.noneFound}</span>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="component-details-set__versions">
      <h2>
        {labels.versionUsedIn}
        {componentSetProps.displayName}
      </h2>
      <div className="component-details-set__versions-container">
        <div className="component-details-set__versions-row">
          {editableComponentVersions()}
          {renderableComponentVersions()}
          {excludedComponentVersions()}
        </div>
        {manageVersionsLauncher()}
      </div>
    </div>
  );
};
